/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    array, func, number,  string,
} from 'prop-types';
import { useUIDSeed } from 'react-uid';
import Slider from 'react-slick';
import { useDispatch } from 'react-redux';
import Button from '@material-ui/core/Button';
import { UpsellType } from '../common/types';
import GraphqlClubProductDetails from './GraphqlClubProductDetails';
import { trackEvent } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

const useStyles = makeStyles((theme) => ({
    carouselContainer: {
        position: 'relative',
        width: '100%',
        '@media only screen and (max-width: 600.5px) ': {
            paddingBottom: '0px',
        },
        '& .slick-disabled': {
            display: 'none !important',
        },
        '& .slick-list': {
            overflow: 'hidden',
            margin: '0 auto',
            width: '100%',
            '&::-webkit-scrollbar-thumb': {
                borderRadius: '10px',
                backgroundColor: 'rgba(156, 156, 156, 1)',  // scroll bar color
            },
            '&::-webkit-scrollbar': {  /* Works on scrollbar for Chrome, Safari and Opera */
                height: '3px',
                backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-track': {
                borderRadius: '10px',
                backgroundColor: 'transparent',
            },
        },
        '& .slick-slide': {
            display: 'inline-block',
            verticalAlign: 'top',
            padding: '6px',
        },
        '& .slick-prev, & .slick-next': {
            display: 'none !important',
        },
    },
    flexCenter: {
        display: 'flex',
        justifyContent: 'center',
    },
    altImage: {
        height: '100%',
        aspectRatio: '107/94',
        width: '100%',
    },
    viewDetails: {
        display: 'block',
        textDecoration: 'underline',
        fontSize: '0.75rem',
        lineHeight: '1.125rem',
        fontWeight: 400,
        cursor: 'pointer',
        textTransform: 'capitalize',
        textAlign: 'left',
        padding: '0px',
        color: theme.palette.cms?.black || '#000000',
        '&:hover': {
            textDecoration: 'underline',
            backgroundColor: 'inherit',
        },
    },
    productName: {
        fontSize: '0.75rem',
        lineHeight: '1.125rem',
        fontWeight: 400,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: '2',
        WebkitBoxOrient: 'vertical',
        minHeight: '35px',
    },
    upsellBoxContainer: {
        display: 'block',
        boxShadow: '0px 0px 5px 0px rgba(47, 47, 47, 0.25)',
        padding: '8px',
        '@media only screen and (max-width: 600px) ': {
            margin: '0px',
        },
    },

    upsellBox: {
        width: '100%',
        position: 'relative',
        marginRight: '35px',
        '& > img': {
            maxWidth: '100%',
        },
        cursor: 'pointer',
        '&:hover span.unselected': {
            background: theme.palette.primaryButton || '#a1001a',
            '& span.priceBlock': {
                display: 'none !important',
            },
            '& span.seeMore': {
                display: 'block',
            },
        },
        '& span.seeMore': {
            display: 'none',
        },
    },
    priceDetails: {
        display: 'block',
        fontSize: '0.875rem',
        lineHeight: '1.125rem',
        fontWeight: 600,
    },
    upsellSelectedBox: {
        boxShadow: '0px 0px 0px 2px rgba(63, 141, 93, 1)',
    },
}));

const GraphqlClubUpgrade = ({
    clubComponents, itemsPerSlide, itemsToShow,  clubUpsells, selectedClubUpsells, changeClubUpsell,
}) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const sliderRef = useRef();
    const [showModal, setShowModal] = useState(false);
    const [showUpsell, setShowUpsell] = useState(null);
    const seed = useUIDSeed();
    const changeClubUpsells = (type) => {
        if (type) {
            changeClubUpsell(type.partNumber);
        } else {
            changeClubUpsell(showUpsell.partNumber);
            setShowModal(false);
        }
    };

    const tracker = (matchObject) => {
        dispatch(trackEvent({
            eventAction: 'upgrade',
            eventLabel: matchObject?.name,
            eventCategory: 'Product Page',
        }));
    };
    const indexMatch = clubUpsells.findIndex((item) => item?.upSellCalculatedPrice === showUpsell?.upSellCalculatedPrice);
    const buttonContent = selectedClubUpsells === showUpsell?.partNumber ? 'Added' : 'Add +';
    useEffect(() => {
        if (sliderRef?.current) {
            sliderRef.current.slickGoTo(0);
        }
    }, [clubComponents]);
    return (
        <div className={classes.flexCenter} key={seed(clubComponents)}>
            <Slider
                dots={false}
                speed={500}
                infinite={false}
                slidesToShow={itemsToShow}
                slidesToScroll={itemsPerSlide}
                ref={sliderRef}
                className={`carousel-container ${classes.carouselContainer}`}
                responsive={[
                    {
                        breakpoint: 1024, // tablet breakpoint
                        settings: {
                            slidesToShow: 3.5,
                            slidesToScroll: 3,
                        },
                    },
                    {
                        breakpoint: 767, // mobile breakpoint
                        settings: {
                            slidesToShow: 2.75,
                            slidesToScroll: 2,
                        },
                    },
                ]}
            >
                {(clubUpsells || [])?.map((upsell, index) => upsell?.upSellDisplayPrice && (
                    <div  key={seed(upsell)}  className={selectedClubUpsells === upsell?.partNumber ? `${classes.upsellSelectedBox} ${classes.upsellBoxContainer}` : classes.upsellBoxContainer}>
                        <div
                            data-selected={selectedClubUpsells}
                            className={classes.upsellBox}
                            data-id={upsell?.partNumber}
                            data-testid={`block-upsell-${upsell?.partNumber}`}
                            onClick={() => {
                                if ((selectedClubUpsells === upsell?.partNumber)) {
                                    changeClubUpsell(showUpsell?.partNumber, true);
                                } else {
                                    changeClubUpsells(upsell);
                                    setShowUpsell(upsell);
                                }
                            }}
                            onKeyPress={() => { }}
                            tabIndex="0"
                            role="link"
                        >
                            <img className={classes.altImage} src={`${upsell?.image?.path}${upsell?.image?.name}x.jpg?width=107&height=94&auto=webp`} alt={upsell.name} />
                            <span className={classes.productName}>{upsell?.name}</span>
                            <span className={classes.priceDetails}>${upsell?.upSellCalculatedPrice}</span>
                        </div>
                        <Button
                            className={classes.viewDetails}
                            onClick={(e) => {
                                e.stopPropagation();
                                tracker(upsell);
                                setShowModal(true);
                                setShowUpsell(upsell);
                            }}
                            data-testid={`viewDetails-${index}`}
                        >
                            View Details
                        </Button>
                    </div>
                ))}

            </Slider>
            {showModal && (
                <GraphqlClubProductDetails
                    open={showModal}
                    indexMatch={indexMatch}
                    data={showUpsell}
                    buttonContent={buttonContent}
                    setOpen={setShowModal}
                    onclick={changeClubUpsells}
                    reciepientProduct={clubUpsells}
                    setData={setShowUpsell}
                    title="UPGRADE DETAILS"
                    disabled={selectedClubUpsells === showUpsell?.partNumber}
                />
            )}
        </div>
    );
};
GraphqlClubUpgrade.propTypes = {
    clubComponents: array,
    itemsToShow: number,
    itemsPerSlide: number,
    clubUpsells: UpsellType,
    selectedClubUpsells: string,
    changeClubUpsell: func,

};
GraphqlClubUpgrade.defaultProps = {
    clubComponents: [],
    selectedClubUpsells: '',
    itemsToShow: 4,
    itemsPerSlide: 4,
    clubUpsells: [],
    changeClubUpsell: () => { },

};
export default GraphqlClubUpgrade;
